import React, { useEffect, useState } from 'react';
import Icon from '../../../../components/common/Icon/Icon';
import Loading from '../../../../components/common/Loading/Loading';
import { ExerciseService } from '../../../../services/ExerciseService';
import ExerciseYouTubePlayer from '../ExerciseYouTubePlayer';
import { useEscapeToClose } from '../../../../hooks/useEscapeToClose';
import styles from './ExerciseVideoModal.module.css';

interface ExerciseVideoModalProps {
  open: boolean;
  exerciseId: number | null;
  exerciseName: string;
  onClose: () => void;
}

const ExerciseVideoModal: React.FC<ExerciseVideoModalProps> = ({
  open,
  exerciseId,
  exerciseName,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!open || exerciseId == null) {
      setVideoUrl(null);
      setLoadError('');
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    ExerciseService.getExerciseById(exerciseId)
      .then((record) => {
        if (!isMounted) {
          return;
        }

        setVideoUrl(record.video_url);
      })
      .catch(() => {
        if (isMounted) {
          setLoadError('Não foi possível carregar o vídeo deste exercício.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [exerciseId, open]);

  useEscapeToClose(onClose, open);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exercise-video-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 id="exercise-video-title" className={styles.title}>{exerciseName}</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Fechar vídeo">
            <Icon name="close" size={20} />
          </button>
        </div>

        <div className={styles.body}>
          {isLoading ? (
            <div className={styles.state}><Loading inline /></div>
          ) : (
            <ExerciseYouTubePlayer
              videoUrl={loadError ? null : videoUrl}
              title={`Vídeo de ${exerciseName}`}
              emptyMessage={loadError || 'Este exercício ainda não tem um vídeo cadastrado.'}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseVideoModal;
